import { Box, Grid, TablePagination, Tooltip } from '@material-ui/core';
import {
  Currency,
  FilterOperator,
  formatDateTimeReadable,
  formatMoney,
  getLoansByCriteria,
  LenderName,
  LoanCriteria,
  LoanCriteriaList,
  LoanStatus,
} from '@nemuru-eng/js-api-client';
import { useEffect, useMemo, useRef, useState } from 'react';
import { generatePath, Link } from 'react-router-dom';

import { FunnelFilters } from '@/components/modules/Funnel/components/FunnelFilters/FunnelFilters';
import { FunnelSectionHeader } from '@/components/modules/Funnel/components/FunnelSectionHeader';
import { MobileFooter } from '@/components/modules/Funnel/components/MobileFooter';
import { NotificationNumberCircle } from '@/components/modules/Funnel/components/NotificationNumberCircle';
import { StepCard } from '@/components/modules/Funnel/components/StepCard';
import { DateTimeIntervals } from '@/components/modules/Funnel/types/DateTimeIntervals';
import { StepStats } from '@/components/modules/Funnel/types/StepStats';
import { combineStats } from '@/components/modules/Funnel/utils/CombineStats';
import { getProductType } from '@/components/modules/Funnel/utils/getProductType';
import { getStepsDefinitions } from '@/components/modules/Funnel/utils/GetStepsDefinitions';
import { getStepStatsFromLoanList } from '@/components/modules/Funnel/utils/GetStepStatsFromLoanList';
import { getStepsUntilNextMilestoneByStepKey } from '@/components/modules/Funnel/utils/GetStepsUntilNextMilestoneByStepKey';
import { ContrastedCard } from '@/components/shared/components/ContrastedCard';
import { DocumentationErrorIcon } from '@/components/shared/components/ErrorIcon/components/DocumentationErrorIcon';
import { LoanApplicationErrorIcon } from '@/components/shared/components/ErrorIcon/components/LoanApplicationErrorIcon';
import { ErrorIcon } from '@/components/shared/components/ErrorIcon/ErrorIcon';
import { Font } from '@/components/shared/components/Font';
import { LenderIcon } from '@/components/shared/components/Logo/LenderLogo/LenderIcon';
import { PendingDataWarning } from '@/components/shared/components/PendingDataWarning';
import { Text } from '@/components/shared/components/Text';
import { LoadingLayer } from '@/components/views/DynamicPageView/components/LoadingLayer';
import { PendingDataType } from '@/config/types';
import { useConfigContext } from '@/contexts/ConfigContext/ConfigContext';
import { useCustomTheme } from '@/hooks/useCustomTheme';
import { useRoleListFilters } from '@/hooks/useRoleListFilters';
import { useScreen } from '@/hooks/useScreen';
import { useUser } from '@/hooks/useUser';
import { getApplicationTypeByLoanCriteria } from '@/utils/getApplicationTypeByLoanCriteria';
import { getDynamicPagePathByProductType } from '@/utils/getDynamicPagePathByProductType';
import { queryStringBuilder } from '@/utils/queryStringBuilder';
import { withPermissionsModule } from '@/utils/withPermissionsModule';

const ITEMS_PER_PAGE = 10;

const AVAILABLE_STATUS_IN_FILTER = [
  LoanStatus.STATUS_INITIAL,
  LoanStatus.STATUS_ANALYSING,
  LoanStatus.STATUS_LENDER_HAS_BEEN_SELECTED,
  LoanStatus.STATUS_CONTRACT_SIGNED,
  LoanStatus.STATUS_DOCS_APPROVED,
];

const Funnel = () => {
  const {
    state: { localeCurrency },
  } = useConfigContext();
  const { palette } = useCustomTheme();
  const { sm, lg, isMobile } = useScreen();

  const filtersByRole = useRoleListFilters();

  const { userEntity } = useUser();

  const [allFunnelLoans, setAllFunnelLoans] = useState(undefined as LoanCriteriaList | undefined);
  const [filteredLoanList, setFilteredLoanList] = useState(undefined as LoanCriteria[] | undefined);
  const [pageNum, setPageNum] = useState(0 as number);
  const [errorsFilter, setErrorsFilter] = useState(false);
  const [incidencesFilter, setIncidencesFilter] = useState(false);

  const [stepTIdsSelectedList, setStepTIdsSelectedList] = useState([] as string[]);

  const triggerRef = useRef<HTMLDivElement>(null);

  const init = async () => {
    const baseQueryFilters = [
      {
        field: 'status',
        operator: FilterOperator.IN,
        value: AVAILABLE_STATUS_IN_FILTER.join(','),
      },
      ...filtersByRole,
    ];

    const queryString = queryStringBuilder({
      page: 1,
      pageSize: 2500,
      filters: baseQueryFilters,
      orderBy: 'updated_at',
      direction: 'desc',
      search: '',
    });

    try {
      const loanList = await getLoansByCriteria({ query: queryString });
      setAllFunnelLoans(loanList);
      setFilteredLoanList(loanList.data);
      setStepTIdsSelectedList(['REQUESTED']);
    } catch (error) {
      // @ts-expect-error: We give a simplified blank value just to force the PendingDataWarning
      setAllFunnelLoans({ data: [] });
    }
  };

  useEffect(() => {
    init();
  }, []);

  const isStepSelected = (stepTId) => stepTIdsSelectedList.some((stepSelected) => stepTId === stepSelected);
  const getStepTidStats = (stepTId) => stepStatsList.find((stepStats) => stepStats.step.tId === stepTId);

  const onStepClick = (e, stepId) => {
    setPageNum(0);
    if (window.event?.ctrlKey || window.event?.shiftKey) {
      setStepTIdsSelectedList([stepId]);
      return;
    }
    if (isStepSelected(stepId)) {
      setStepTIdsSelectedList((s) => {
        return [...s.filter((oldStep) => oldStep !== stepId)];
      });
    } else {
      setStepTIdsSelectedList((s) => [stepId, ...s]);
    }
  };

  const getMilestoneStepsByMilestoneStepId = (stepId) => {
    const associatedMilestone = stepsDefinitions.find((step) => step.tId === stepId)?.associatedMilestone;
    const associatedMilestoneIndex = stepsDefinitions.findIndex(
      (step) => step.associatedMilestone === associatedMilestone,
    );
    return getStepsUntilNextMilestoneByStepKey(associatedMilestoneIndex, selectedProductType).map((s) => s.tId);
  };

  const onMilestoneClick = (_, stepId) => {
    setPageNum(0);
    const milestoneSteps = getMilestoneStepsByMilestoneStepId(stepId);

    const foreignMilestoneSteps = stepTIdsSelectedList.filter((stepTid) => {
      return !milestoneSteps.some((milestoneStep) => milestoneStep === stepTid);
    });
    if (window.event?.ctrlKey || window.event?.shiftKey) {
      setStepTIdsSelectedList(milestoneSteps);
      return;
    }
    const areAllMilestoneStepsSelected = () => {
      return !milestoneSteps.some((step) => !isStepSelected(step));
    };

    if (areAllMilestoneStepsSelected()) {
      setStepTIdsSelectedList(foreignMilestoneSteps);
    } else {
      setStepTIdsSelectedList((steps) => [...steps, ...milestoneSteps]);
    }
  };

  const getSelectedListName = () => {
    stepTIdsSelectedList.sort(
      (a, b) =>
        stepsDefinitions.findIndex((step) => step.tId === a) - stepsDefinitions.findIndex((step) => step.tId === b),
    );
    const firstStepTId = stepTIdsSelectedList[0];
    const milestone = getStepTidStats(firstStepTId)?.step.associatedMilestone;
    const stepsOfMilestone = getMilestoneStepsByMilestoneStepId(firstStepTId);
    const areAllMilestoneStepsSelected = milestone && !stepsOfMilestone.some((step) => !isStepSelected(step));

    switch (stepTIdsSelectedList.length) {
      case 0:
        return 'NO_RESULTS_TO_SHOW';
      case 1:
        return firstStepTId;
      default:
        if (areAllMilestoneStepsSelected && stepsOfMilestone.length === stepTIdsSelectedList.length) {
          return milestone;
        }
        return 'CUSTOM_GROUP';
    }
  };

  const getFirstProductFilter = () => {
    const allowedProducts = userEntity?.allowedProducts;
    if (allowedProducts && allowedProducts.length === 1) {
      return allowedProducts[0];
    }
    return 0;
  };

  const [productFilter, setProductFilter] = useState(getFirstProductFilter());
  const [dateFilter, setDateFilter] = useState(DateTimeIntervals[0].minDateDiff);
  const [lenderNameFilter, setLenderNameFilter] = useState([] as unknown as LenderName[]);
  const [minAmountFilter, setMinAmountFilter] = useState(0);
  const [maxAmountFilter, setMaxAmountFilter] = useState(0);

  // FILTERs

  const onChangeProduct = (_, input) => {
    const selectedProduct = input.props.value;
    setProductFilter(selectedProduct);
  };

  const onChangeDateInterval = (_, input) => {
    setDateFilter(input.props.value);
  };

  const onChangeMinAmount = (e) => {
    setMinAmountFilter(e.target.value);
  };

  const onChangeMaxAmount = (e) => {
    setMaxAmountFilter(e.target.value);
  };

  const getFilteredLoanList = () => {
    let filteredLoanList: LoanCriteria[] | undefined;
    filteredLoanList = productFilter
      ? allFunnelLoans?.data.filter((loan) => getApplicationTypeByLoanCriteria(loan) === productFilter)
      : allFunnelLoans?.data;
    filteredLoanList =
      filteredLoanList && dateFilter
        ? filteredLoanList.filter((loan) => Date.now() - loan.updatedAt.getTime() > dateFilter)
        : filteredLoanList;

    filteredLoanList =
      filteredLoanList && lenderNameFilter.length > 0
        ? filteredLoanList.filter((loan) => {
            if (loan.loanApplicationSelected?.lenderName)
              return lenderNameFilter.includes(loan.loanApplicationSelected?.lenderName as LenderName);
          })
        : filteredLoanList;
    filteredLoanList =
      filteredLoanList && minAmountFilter
        ? filteredLoanList.filter((loan) => loan.loanConditions.principal.amount > minAmountFilter)
        : filteredLoanList;
    filteredLoanList =
      filteredLoanList && maxAmountFilter
        ? filteredLoanList.filter((loan) => loan.loanConditions.principal.amount < maxAmountFilter)
        : filteredLoanList;

    if (errorsFilter || incidencesFilter) {
      const alertFilter = (loan: LoanCriteria) => {
        if (loan.errorDetected && errorsFilter) return true;
        if (loan.loanApplicationSelected?.isDocumentationWithIncidence && incidencesFilter) return true;
        return false;
      };
      filteredLoanList = filteredLoanList && filteredLoanList.filter((loan) => alertFilter(loan));
    }
    return filteredLoanList;
  };

  useEffect(() => {
    setPageNum(0);
    setFilteredLoanList(getFilteredLoanList());
  }, [productFilter, dateFilter, lenderNameFilter, minAmountFilter, maxAmountFilter, errorsFilter, incidencesFilter]);

  const selectedProductType = getProductType(productFilter);
  const stepsDefinitions = getStepsDefinitions(selectedProductType);
  const stepStatsList = useMemo<StepStats[]>(
    () => (filteredLoanList ? getStepStatsFromLoanList(filteredLoanList, selectedProductType) : []),
    [filteredLoanList, selectedProductType],
  );

  const stepsListSelectedStats = stepStatsList.filter((stepStats) => isStepSelected(stepStats.step.tId));
  const combinedStats = combineStats(stepsListSelectedStats);
  const selectedList = combinedStats.loans.sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime());
  const mainCurrency = localeCurrency || Currency.EUR;
  const isUserMCA = userEntity && userEntity.isRoleMerchantChannelOrAgent();

  if (allFunnelLoans?.data.length === 0) {
    return <PendingDataWarning type={PendingDataType.NO_FUNNEL_DATA} />;
  }

  return (
    <>
      <LoadingLayer loading={!allFunnelLoans} />
      {allFunnelLoans && (
        <>
          <Grid container spacing={3} className={'appear-anim'}>
            <Box p={3} width={'100%'}>
              <FunnelFilters
                onChangeProduct={onChangeProduct}
                productFilter={productFilter}
                onChangeDateInterval={onChangeDateInterval}
                dateFilter={dateFilter}
                onChangeLender={setLenderNameFilter}
                lenderNameFilter={lenderNameFilter}
                onChangeMinAmount={onChangeMinAmount}
                currency={mainCurrency}
                onChangeMaxAmount={onChangeMaxAmount}
                incidenceFilter={incidencesFilter}
                onChangeIncidencesFilter={() => {
                  setIncidencesFilter(!incidencesFilter);
                }}
                errorsFilter={errorsFilter}
                onChangeErrorsFilter={() => {
                  setErrorsFilter(!errorsFilter);
                }}
              />
            </Box>
            <Grid item xs={12} lg={6}>
              <Box ml={1} mt={isMobile ? 3 : 0} mb={isMobile ? 2 : 5}>
                <Font variant={'h3'} color={'title'}>
                  <Text tId={'FUNNEL'} />
                </Font>
              </Box>
              <div>
                <Box
                  maxHeight={isMobile ? 'unset' : 'max(690px,calc(100dvh - 380px))'}
                  overflow={sm ? 'visible auto' : 'unset'}
                  style={
                    {
                      scrollbarWidth: 'none',
                      '&::WebkitScrollbar': {
                        display: 'none',
                      },
                    } as React.CSSProperties
                  }
                >
                  {stepStatsList.map((stepStatsToBeMilestone, stepKey) => {
                    const stepsUntilNextMilestone = getStepsUntilNextMilestoneByStepKey(stepKey, selectedProductType);
                    return (
                      stepStatsToBeMilestone.step.associatedMilestone && (
                        <Box
                          mb={stepKey < stepStatsList.length ? 2 : 0}
                          key={`${stepStatsToBeMilestone.step.tId}-${stepKey}`}
                        >
                          <ContrastedCard
                            onClick={(e) => onMilestoneClick(e, stepStatsToBeMilestone.step.tId)}
                            boxProps={{
                              style: {
                                padding: '4px 0 4px 12px',
                              },
                            }}
                            paperProps={{
                              style: {
                                marginBottom: '0',
                                marginRight: '38px',
                                width: 'auto',
                                boxShadow: 'none',
                                backgroundColor: 'transparent',
                              },
                            }}
                            endIcon={undefined}
                          >
                            <Box display={'flex'} mr={5} justifyContent={'space-between'}>
                              <Text tId={stepStatsToBeMilestone.step.associatedMilestone} strong />
                            </Box>
                          </ContrastedCard>
                          <Box display={'flex'} mt={2} mb={1} maxWidth={'100%'}>
                            <Box display={'flex'} flexDirection={'column'} flex={1}>
                              {stepsUntilNextMilestone.map((step) => {
                                const currentStepStats = getStepTidStats(step.tId);
                                const isSelected = isStepSelected(step.tId);
                                const alerts =
                                  currentStepStats && currentStepStats.errors + currentStepStats.incidences;
                                return (
                                  <Box display={'flex'} key={`step-${step.tId}-${stepKey}`} position={'relative'}>
                                    <StepCard
                                      stepStats={currentStepStats}
                                      onClick={(e) => onStepClick(e, currentStepStats?.step.tId)}
                                      currency={mainCurrency}
                                      isSelected={isSelected}
                                    />
                                    <Box
                                      ml={1}
                                      minWidth={'30px'}
                                      display={'flex'}
                                      alignItems={'center'}
                                      justifyContent={'center'}
                                      style={
                                        sm
                                          ? {}
                                          : {
                                              position: 'absolute',
                                              top: '0px',
                                              right: '0px',
                                              transform: 'translate(50%,-50%)',
                                            }
                                      }
                                    >
                                      {!!alerts && !isUserMCA && (
                                        <Tooltip
                                          title={
                                            <>
                                              <Box display={'flex'} alignItems={'center'}>
                                                <LoanApplicationErrorIcon />{' '}
                                                <Box mx={1}>
                                                  <strong>{currentStepStats.errors} </strong>
                                                </Box>
                                                <Text tId={'LOAN_APPLICATION_ERRORS'} />
                                              </Box>
                                              <Box mt={1} display={'flex'} alignItems={'center'}>
                                                <DocumentationErrorIcon />
                                                <Box mx={1}>
                                                  <strong> {currentStepStats.incidences} </strong>
                                                </Box>
                                                <Text tId={'DOCUMENTATION_INCIDENCES'} />
                                              </Box>
                                            </>
                                          }
                                        >
                                          <Box>
                                            <NotificationNumberCircle num={alerts} />
                                          </Box>
                                        </Tooltip>
                                      )}
                                    </Box>
                                  </Box>
                                );
                              })}
                            </Box>
                          </Box>
                        </Box>
                      )
                    );
                  })}
                </Box>
              </div>
            </Grid>
            <Grid item xs={12} lg={6} ref={triggerRef} style={lg ? {} : { paddingTop: '110px', marginTop: '-90px' }}>
              <Box pb={'22px'}>
                <FunnelSectionHeader
                  length={combinedStats?.loans.length}
                  totalAmount={combinedStats?.totalAmount}
                  titleTId={getSelectedListName()}
                  currency={mainCurrency}
                />
              </Box>
              {selectedList &&
                selectedList.slice(pageNum * ITEMS_PER_PAGE, pageNum * ITEMS_PER_PAGE + 10).map((loan) => {
                  return (
                    <Box key={`link-${loan.id}`}>
                      <Link
                        to={generatePath(
                          getDynamicPagePathByProductType(loan.product, loan.assetType, loan.onboardingFulfilledBy),
                          { loanId: loan.id },
                        )}
                      >
                        <ContrastedCard
                          onClick={() => true}
                          boxProps={{
                            style: {
                              padding: '3px',
                            },
                          }}
                          paperProps={{
                            style: {
                              boxShadow: 'none !important',
                              marginBottom: '10px',
                            },
                          }}
                        >
                          <Box
                            width={sm ? '30px' : '22px'}
                            ml={sm ? '16px' : '8px'}
                            mr={'8px'}
                            display={'flex'}
                            alignItems={'center'}
                          >
                            {loan.loanApplicationSelected?.lenderName ? (
                              <LenderIcon
                                size={sm ? 22 : 18}
                                lenderName={loan.loanApplicationSelected.lenderName! as LenderName}
                              />
                            ) : (
                              <Box color={palette.grey[300]}>
                                <span className="material-icons-outlined">remove</span>
                              </Box>
                            )}
                          </Box>
                          <Box
                            flex={1}
                            textAlign={'left'}
                            className={'overflow-text-ellipsis'}
                            style={{
                              marginRight: sm ? '8px' : '4px',
                            }}
                          >
                            <Font variant={'subtitle2'}>
                              {`${loan.borrowerInvitation.fullName.firstName} ${loan.borrowerInvitation.fullName.lastName}`}
                            </Font>

                            <Box mt={'-10px'} className={'overflow-text-ellipsis'}>
                              <Font variant={'caption'}> {formatDateTimeReadable(loan.updatedAt)}</Font>
                            </Box>
                          </Box>
                          <Box
                            textAlign={'left'}
                            display={'flex'}
                            alignItems={'center'}
                            pr={isUserMCA ? (isMobile ? 1 : 2) : 0}
                            pl={2}
                          >
                            <Font variant={'subtitle2'}>
                              {formatMoney({
                                value: loan.loanConditions.principal.amount,
                                currency: loan.loanConditions.principal.currency,
                              })}
                            </Font>
                          </Box>
                          {!isUserMCA && (
                            <Box display={'flex'} alignItems={'center'} ml={sm ? 2 : 1} width={sm ? '30px' : '22px'}>
                              <ErrorIcon
                                errorDetected={loan.errorDetected}
                                isDocumentationWithIncidence={
                                  loan.loanApplicationSelected?.isDocumentationWithIncidence
                                }
                              />
                            </Box>
                          )}
                        </ContrastedCard>
                      </Link>
                    </Box>
                  );
                })}
              <TablePagination
                component="div"
                count={selectedList?.length}
                page={pageNum}
                onPageChange={(_, num) => setPageNum(num)}
                rowsPerPage={ITEMS_PER_PAGE}
                rowsPerPageOptions={[ITEMS_PER_PAGE]}
              />
            </Grid>
          </Grid>
          {!lg && <MobileFooter triggerRef={triggerRef} />}
        </>
      )}
    </>
  );
};

export const FunnelModule = withPermissionsModule(Funnel);
